/**
 * Admin Activity Menu Component
 * Displays admin navigation and activities in a rich, professional manner
 * Only visible to authorized admin users
 */

import React, { useMemo } from "react";
import { Stack, Icon, mergeStyles } from "@fluentui/react";
import { Box, Typography, Divider, Chip, Paper } from "@mui/material";
import {
    Database20Regular,
    Shield20Regular,
    CheckmarkCircle20Regular
} from "@fluentui/react-icons";
import { useNavigate } from "react-router-dom";
import { isUserAuthorizedForCacheAdmin, CACHE_ADMIN_AUTHORIZED_USERS } from "../config/CacheAdminConfig";

interface AdminActivityItem {
    id: string;
    label: string;
    description: string;
    icon: string;
    route: string;
    badge?: string;
    color: string;
}

const ADMIN_ACTIVITIES: AdminActivityItem[] = [
    {
        id: "cache-admin",
        label: "Cache Administration",
        description: "Manage caches • Clear Redis/Static Data",
        icon: "Database",
        route: "/cacheadmin",
        badge: "Admin",
        color: "#0078D4"
    }
];

const AdminActivityMenu: React.FC = () => {
    const navigate = useNavigate();
    const userEmail = sessionStorage.getItem("loginUserName");
    const isAuthorized = useMemo(() => isUserAuthorizedForCacheAdmin(userEmail), [userEmail]);

    // Only render if user is authorized
    if (!isAuthorized) {
        return null;
    }

    const containerStyle = mergeStyles({
        padding: "12px 0",
        borderTop: "1px solid #E1E5EB",
        marginTop: "12px"
    });

    const getIconComponent = (iconName: string) => {
        return <Database20Regular style={{ fontSize: 20, color: "#0078D4" }} />;
    };

    const handleActivityClick = (route: string) => {
        navigate(route);
    };

    return (
        <div className={containerStyle}>
            {/* Section Header */}
            <Box sx={{ px: 2, mb: 1.5 }}>
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                    <Shield20Regular style={{ fontSize: 16, color: "#0078D4" }} />
                    <Typography
                        variant="caption"
                        sx={{
                            fontWeight: 700,
                            fontSize: 11,
                            color: "#605E5C",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px"
                        }}
                    >
                        Admin Activities
                    </Typography>
                </Stack>
            </Box>

            <Divider sx={{ my: 1, mx: 0 }} />

            {/* Activities List */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
                {ADMIN_ACTIVITIES.map((activity, index) => (
                    <Box
                        key={activity.id}
                        onClick={() => handleActivityClick(activity.route)}
                        sx={{
                            px: 2,
                            py: 1,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            backgroundColor: "transparent",
                            "&:hover": {
                                backgroundColor: "#F5F7FA",
                                transform: "translateX(4px)"
                            }
                        }}
                    >
                        {/* Icon with colored circle background */}
                        <Box
                            sx={{
                                width: 32,
                                height: 32,
                                borderRadius: "8px",
                                backgroundColor: `${activity.color}14`,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0
                            }}
                        >
                            {getIconComponent(activity.icon)}
                        </Box>

                        {/* Content */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 6 }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: 12,
                                        color: "#1A1A2E",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap"
                                    }}
                                >
                                    {activity.label}
                                </Typography>
                                {activity.badge && (
                                    <Chip
                                        label={activity.badge}
                                        size="small"
                                        sx={{
                                            height: 16,
                                            fontSize: 9,
                                            fontWeight: 700,
                                            backgroundColor: `${activity.color}20`,
                                            color: activity.color,
                                            border: `1px solid ${activity.color}40`
                                        }}
                                    />
                                )}
                            </Stack>
                            <Typography
                                variant="caption"
                                sx={{
                                    fontSize: 10,
                                    color: "#8A8886",
                                    display: "block",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap"
                                }}
                            >
                                {activity.description}
                            </Typography>
                        </Box>

                        {/* Chevron Icon */}
                        <Icon
                            iconName="ChevronRight"
                            styles={{
                                root: {
                                    fontSize: 14,
                                    color: "#8A8886",
                                    flexShrink: 0
                                }
                            }}
                        />
                    </Box>
                ))}
            </Box>

            {/* Admin Badge at bottom */}
            <Divider sx={{ my: 1, mx: 0 }} />
            <Box sx={{ px: 2, py: 1 }}>
                <Paper
                    elevation={0}
                    sx={{
                        backgroundColor: "#EFF6FC",
                        border: "1px solid #C7E0F4",
                        borderRadius: "6px",
                        p: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 1
                    }}
                >
                    <CheckmarkCircle20Regular style={{ fontSize: 14, color: "#0078D4" }} />
                    <Typography
                        variant="caption"
                        sx={{
                            fontSize: 10,
                            color: "#0F6CBD",
                            fontWeight: 600
                        }}
                    >
                        Admin Access Granted
                    </Typography>
                </Paper>
            </Box>
        </div>
    );
};

export default AdminActivityMenu;
